import { useState } from 'react'
import { router } from '@inertiajs/react'
import { MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import TimelineItem from './TimelineItem'
import { TimeAgo } from '@/components/TimeAgo'
import { copyText } from './copyText'
import { submission_comment_path } from '@/lib/routes'
import { type CellLink } from './reviewComments'

export interface CommentProps {
  id: string
  author: string
  author_avatar_url?: string | null
  body: string
  at: string
  // Set on a review cell comment: the number it was written on.
  on?: string | null
  // Set when that cell still has a row on the Plan changes tab.
  cell?: CellLink | null
  mine: boolean
  // "conversation" = a plain comment on the submission; "review" = a cell
  // comment. Each is changed through its own route.
  kind: 'conversation' | 'review'
}

// A posted conversation comment — same anatomy as the submission card. The
// author gets Edit and Delete in the "..." menu while the review is open.
// A cell comment needs `reviewCommentPath` (its own route); without it the
// card shows no Edit or Delete for one. With `onOpenCell`, the cell's name
// links to its comments on the Plan changes tab.
export default function CommentCard({ comment, submissionId, readOnly = false, reviewCommentPath, onOpenCell }: {
  comment: CommentProps
  submissionId: string
  readOnly?: boolean
  reviewCommentPath?: (id: string) => string
  onOpenCell?: (cell: CellLink) => void
}) {
  const [mode, setMode] = useState<'view' | 'edit' | 'delete'>('view')
  const [draft, setDraft] = useState('')
  const path = comment.kind === 'review'
    ? reviewCommentPath?.(comment.id)
    : submission_comment_path(submissionId, comment.id)
  const canChange = comment.mine && !readOnly && path != null

  const startEdit = () => {
    setDraft(comment.body)
    setMode('edit')
  }
  const update = () => {
    const params = comment.kind === 'review' ? { body: draft } : { message: draft }
    if (path) router.patch(path, params, { preserveScroll: true })
    setMode('view')
  }
  const destroy = () => path && router.delete(path, { preserveScroll: true })

  return (
    <TimelineItem name={comment.author} avatarUrl={comment.author_avatar_url}>
      <div className="flex items-center gap-3 border-b border-border px-4 py-2.5 text-sm text-foreground">
        <div className="min-w-0">
          <span className="font-semibold">{comment.author}</span>
          <span> commented</span>
          <span className="text-muted-foreground"> · <TimeAgo at={comment.at} /></span>
          {comment.on && (
            <span> · on {comment.cell && onOpenCell ? (
              <Button variant="link" className="h-auto p-0 text-sm font-medium text-primary underline" onClick={() => comment.cell && onOpenCell(comment.cell)}>
                {comment.on}
              </Button>
            ) : <span className="font-medium">{comment.on}</span>}</span>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Comment actions"
              className="ml-auto h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => copyText(window.location.href)}>Copy link</DropdownMenuItem>
            {canChange && <DropdownMenuItem onClick={startEdit}>Edit</DropdownMenuItem>}
            {canChange && (
              <DropdownMenuItem className="text-destructive" onClick={() => setMode('delete')}>Delete</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {mode === 'edit' ? (
        <div className="flex flex-col gap-2 px-4 py-3">
          <Textarea
            rows={4}
            className="min-h-20 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-chart-muted"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Leave a comment"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setMode('view')}>Cancel</Button>
            <Button size="sm" disabled={draft.trim().length === 0} onClick={update}>Update comment</Button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-line px-4 py-3 text-[13px] leading-relaxed text-foreground">
          {comment.body}
        </p>
      )}

      {mode === 'delete' && (
        <div className="flex items-center gap-2 border-t border-border bg-destructive/5 px-4 py-2.5 text-sm text-foreground">
          <span>Delete this comment?</span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setMode('view')}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={destroy}>Delete</Button>
          </div>
        </div>
      )}
    </TimelineItem>
  )
}
